/**
 * CopilotProvider
 *
 * React context that manages copilot chat state: message history,
 * session tracking, loading state, and the sendMessage API call.
 *
 * Wraps children with CopilotContext so any descendant can access
 * the chat state and dispatch messages to the backend.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react'
import axios from 'axios'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CopilotMessage {
  role: 'user' | 'bot'
  text: string
  data?: Record<string, unknown>[]
  format?: 'text' | 'table' | 'list'
  suggestions?: string[]
}

interface CopilotContextValue {
  messages: CopilotMessage[]
  loading: boolean
  sendMessage: (text: string) => Promise<void>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CopilotContext = createContext<CopilotContextValue | null>(null)

export const useCopilot = (): CopilotContextValue => {
  const ctx = useContext(CopilotContext)
  if (!ctx) {
    throw new Error('useCopilot must be used within a CopilotProvider')
  }
  return ctx
}

// ---------------------------------------------------------------------------
// API config
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const COPILOT_URL = `${API_BASE}/copilot/chat`

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CopilotProviderProps {
  children: React.ReactNode
}

export const CopilotProvider: React.FC<CopilotProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([])
  const [loading, setLoading] = useState(false)
  const sessionIdRef = useRef<string | null>(null)

  const sendMessage = useCallback(async (text: string) => {
    const userMsg: CopilotMessage = { role: 'user', text }
    setMessages(prev => [...prev, userMsg])
    setLoading(true)

    try {
      const payload: Record<string, unknown> = { message: text }
      if (sessionIdRef.current) {
        payload.session_id = sessionIdRef.current
      }

      const { data } = await axios.post(COPILOT_URL, payload, {
        headers: { 'Content-Type': 'application/json' },
      })

      // Persist session
      if (data.session_id) {
        sessionIdRef.current = data.session_id
      }

      const botMsg: CopilotMessage = {
        role: 'bot',
        text: data.answer ?? data.reply ?? data.message ?? '',
        data: data.data,
        format: data.format ?? 'text',
        suggestions: data.suggestions,
      }

      setMessages(prev => [...prev, botMsg])
    } catch (err: unknown) {
      const fallback =
        axios.isAxiosError(err) && err.response?.data?.detail
          ? String(err.response.data.detail)
          : 'Something went wrong. Please try again.'

      setMessages(prev => [
        ...prev,
        { role: 'bot', text: fallback, format: 'text' },
      ])
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <CopilotContext.Provider value={{ messages, loading, sendMessage }}>
      {children}
    </CopilotContext.Provider>
  )
}

export default CopilotProvider
